/**
 * orthoConverter.ts
 * Three-layer rule-based Post-AO → Pre-AO orthographic converter for PT-PT.
 * Targets Luso-African markets (Angola, Mozambique).
 * Based on Acordo Ortográfico da Língua Portuguesa 1990/2009 (public domain treaty).
 *
 * ARCHITECTURE — three processing layers applied in strict order:
 *   Layer 2  Explicit word pairs  (verbal nouns: t→ç mutation hides root)
 *   Layer 1  Root table           (ct/pt-cluster restorations, all inflections)
 *   Layer 3  Pattern rules        (circumflex restoration for crer/ver/ler/dar)
 *
 * USAGE:
 *   toPreAO(text)           → convert Post-AO MT output to Pre-AO (Angola/Mozambique)
 *   toPostAO(text)          → convert Pre-AO source to Post-AO
 *   detectPostAOForms(text) → find Post-AO forms in a segment
 *   detectBRVariants(text)  → find PT-BR lexical forms in MT output
 *   analyseSegment(...)     → full quality-gate pipeline
 */

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

export type Confidence = "high" | "medium";

export interface ConversionRule {
    postAO: string;
    preAO: string;
    confidence: Confidence;
    notes?: string;
}

export interface DetectedForm {
    word: string;
    position: number;
    preAOForm?: string;
    postAOForm?: string;
    confidence: Confidence;
    notes?: string;
}

export interface ConversionResult {
    text: string;
    flags: DetectedForm[];
    changeCount: number;
}

export interface LexicalVariant {
    ptBR: string;
    ptPT: string;
    domains: string[];
    register: "formal" | "informal" | "neutral";
    confidence: Confidence;
    notes?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 1 — Root table
//
// Each pair replaces a Post-AO root with its Pre-AO form.
// No word boundary is required — compound prefixes (pro-, retro-, in-, etc.)
// are handled automatically because the root appears as a substring.
//
// Order: longer/more-specific roots that share a prefix must appear BEFORE
// shorter ones only when the shorter root is NOT a prefix of the longer one
// (i.e. conflicts). Within the ct/pt groups, the listed order is safe.
// ─────────────────────────────────────────────────────────────────────────────

const LAYER1_ROOTS: ReadonlyArray<readonly [post: string, pre: string]> = [
    // ── ct-cluster restorations ───────────────────────────────────────────────
    ["adot",      "adopt"    ], // adotar→adoptar, adotado→adoptado, adotou→adoptou
    ["diret",     "direct"   ], // direto→directo, diretor→director, diretamente→directamente
    ["fato",      "facto"    ], // fato→facto, fator→factor, fatores→factores
    ["fatur",     "factur"   ], // fatura→factura, faturar→facturar, faturação→facturação
    ["infet",     "infect"   ], // infetado→infectado, infetar→infectar
    ["detet",     "detect"   ], // detetar→detectar, detetado→detectado, detetive→detective
    ["colet",     "colect"   ], // coletivo→colectivo, coletar→colectar
    ["afet",      "afect"    ], // afetar→afectar, afetado→afectado, afetação→afectação
    ["efetiv",    "efectiv"  ], // efetivo→efectivo, efetivamente→efectivamente
    ["efetu",     "efectu"   ], // efetuar→efectuar, efetuado→efectuado
    ["exat",      "exact"    ], // exato→exacto, exatamente→exactamente, exatidão→exactidão
    ["seto",      "secto"    ], // setor→sector, setores→sectores, setorial→sectorial
    ["projet",    "project"  ], // projeto→projecto, projetar→projectar, projetos→projectos
    ["objetiv",   "objectiv" ], // objetivo→objectivo, objetivamente→objectivamente
    ["objeto",    "objecto"  ], // objeto→objecto, objetos→objectos
    ["adjetiv",   "adjectiv" ], // adjetivo→adjectivo
    ["respetiv",  "respectiv"], // respetivo→respectivo, respetivamente→respectivamente
    ["seletiv",   "selectiv" ], // seletivo→selectivo, seletividade→selectividade
    // eletr: longer eletric/elétric variants listed first so they are not
    // shadowed when order matters in future refactors; eletr handles the rest.
    ["eletric",   "electric" ], // eletricidade→electricidade
    ["elétric",   "eléctric" ], // elétrico→eléctrico (note accented é)
    ["eletr",     "electr"   ], // eletrão→electrão, eletrónica→electrónica
    // ── pt-cluster restorations ───────────────────────────────────────────────
    ["ótim",      "óptim"    ], // ótimo→óptimo, ótima→óptima, ótimas→óptimas
    ["otimis",    "optimis"  ], // otimismo→optimismo, otimista→optimista
    ["otimiz",    "optimiz"  ], // otimizar→optimizar, otimização→optimização
    ["batiz",     "baptiz"   ], // batizado→baptizado, batizar→baptizar
    ["batism",    "baptism"  ], // batismo→baptismo, batismal→baptismal
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 2 — Explicit word pairs
//
// Verbal nouns where the stem-final t mutated to ç before -ão, making the root
// invisible to Layer 1 (e.g. adot- → ado-ç-ão, not adot-ão).
// MUST be processed BEFORE Layer 1. Plurals appear before their singular
// counterparts within each row to prevent the singular regex from consuming
// characters that are part of the plural suffix.
// ─────────────────────────────────────────────────────────────────────────────

const LAYER2_PAIRS: ReadonlyArray<readonly [post: string, pre: string]> = [
    // plurals first ────────────────────────────────────────────────────────────
    ["adoções",      "adopções"    ],
    ["perceções",    "percepções"  ],
    ["conceções",    "concepções"  ],
    ["receções",     "recepções"   ],
    ["deteções",     "detecções"   ],
    ["infeções",     "infecções"   ],
    ["coleções",     "colecções"   ],
    ["seleções",     "selecções"   ],
    ["direções",     "direcções"   ],
    ["redações",     "redacções"   ],
    ["ações",        "acções"      ],
    ["reações",      "reacções"    ],
    ["transações",   "transacções" ],
    ["atrações",     "atracções"   ],
    ["distrações",   "distracções" ],
    ["satisfações",  "satisfacções"],
    ["contrações",   "contracções" ],
    ["extrações",    "extracções"  ],
    // singulars ────────────────────────────────────────────────────────────────
    ["adoção",       "adopção"     ],
    ["perceção",     "percepção"   ],
    ["conceção",     "concepção"   ],
    ["receção",      "recepção"    ],
    ["deteção",      "detecção"    ],
    ["infeção",      "infecção"    ],
    ["coleção",      "colecção"    ],
    ["seleção",      "selecção"    ],
    ["direção",      "direcção"    ],
    ["redação",      "redacção"    ],
    ["ação",         "acção"       ],
    ["reação",       "reacção"     ],
    ["transação",    "transacção"  ],
    ["atração",      "atracção"    ],
    ["distração",    "distracção"  ],
    ["satisfação",   "satisfacção" ],
    ["contração",    "contracção"  ],
    ["extração",     "extracção"   ],
    ["atividades",    "actividades"  ],
    ["atividade",     "actividade"   ],
    ["ativação",      "activação"    ],
    ["ativadas",      "activadas"    ],
    ["ativados",      "activados"    ],
    ["ativada",       "activada"     ],
    ["ativado",       "activado"     ],
    ["ativar",        "activar"      ],
    ["ativos",        "activos"      ],
    ["ativas",        "activas"      ],
    ["ativa",         "activa"       ],
    ["ativo",         "activo"       ],
    ["inatividade",   "inactividade" ],
    ["inativos",      "inactivos"    ],
    ["inativas",      "inactivas"    ],
    ["inativo",       "inactivo"     ],
    ["proativos",     "proactivos"   ],
    ["proativas",     "proactivas"   ],
    ["proativa",      "proactiva"    ],
    ["proativo",      "proactivo"    ],
    ["retroativos",   "retroactivos" ],
    ["retroativas",   "retroactivas" ],
    ["retroativa",    "retroactiva"  ],
    ["retroativo",    "retroactivo"  ],
    ["reativos",      "reactivos"    ],
    ["reativas",      "reactivas"    ],
    ["reativa",       "reactiva"     ],
    ["reativo",       "reactivo"     ],
    ["radioativos",   "radioactivos" ],
    ["radioativas",   "radioactivas" ],
    ["radioativa",    "radioactiva"  ],
    ["radioativo",    "radioactivo"  ],
    ["interativas",   "interactivas" ],
    ["interativa",    "interactiva"  ],
    ["interativos",   "interactivos" ],
    ["interativo",    "interactivo"  ],
    ["hiperativas",   "hiperactivas" ],
    ["hiperativa",    "hiperactiva"  ],
    ["hiperativos",   "hiperactivos" ],
    ["hiperativo",    "hiperactivo"  ],
    ["atrizes",       "actrizes"     ],
    ["atores",        "actores"      ],
    ["atriz",         "actriz"       ],
    ["ator",          "actor"        ],
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 3 — Pattern rules
//
// AO1990 removed the circumflex accent from 3rd-person plural of crer, ver,
// ler, dar. Pre-AO restores it.
// ─────────────────────────────────────────────────────────────────────────────

const LAYER3_PAIRS: ReadonlyArray<readonly [post: string, pre: string]> = [
    ["creem", "crêem"],
    ["veem",  "vêem" ],
    ["leem",  "lêem" ],
    ["deem",  "dêem" ],
    // ── Month capitalisation (Pre-AO requires capital initial) ────────────────
    ["janeiro",       "Janeiro"      ],
    ["fevereiro",     "Fevereiro"    ],
    ["março",         "Março"        ],
    ["abril",         "Abril"        ],
    ["maio",          "Maio"         ],
    ["junho",         "Junho"        ],
    ["julho",         "Julho"        ],
    ["agosto",        "Agosto"       ],
    ["setembro",      "Setembro"     ],
    ["outubro",       "Outubro"      ],
    ["novembro",      "Novembro"     ],
    ["dezembro",      "Dezembro"     ],
    // ── Weekday capitalisation (Pre-AO: first element only) ───────────────────
    ["segunda-feira", "Segunda-feira"],
    ["terça-feira",   "Terça-feira"  ],
    ["quarta-feira",  "Quarta-feira" ],
    ["quinta-feira",  "Quinta-feira" ],
    ["sexta-feira",   "Sexta-feira"  ],
    ["sábado",        "Sábado"       ],
    ["domingo",       "Domingo"      ],
    ["segunda",       "Segunda"      ],
    ["terça",         "Terça"        ],
    ["quarta",        "Quarta"       ],
    ["quinta",        "Quinta"       ],
    ["sexta",         "Sexta"        ],
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// PROTECTED WORDS (pass-through guard — documentation)
//
//   contacto, contactos, contactar, contactou
//     The 'c' is etymologically pronounced in PT-PT; AO1990 only dropped it
//     in PT-BR (contato).  DeepL PT-PT output already uses the Pre-AO 'c'
//     form, so no rule in the three layers will touch these words.
//     Listed here explicitly so the intent is auditable.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// CASE-PRESERVING HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Escapes a literal string for use inside a RegExp. */
function escRe(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Applies a root substitution in lowercase, Title-case, and UPPER-CASE forms.
 * No word boundary — this intentionally handles compound forms:
 *   proativo → proactivo   (ativ mid-word after 'pro')
 *   retroativo → retroactivo
 *
 *   lowercase:   /ativ/g   → 'activ'
 *   Title case:  /Ativ/g   → 'Activ'
 *   UPPER CASE:  /ATIV/g   → 'ACTIV'
 */
function applyRoot(text: string, post: string, pre: string): string {
    const postTitle = post.charAt(0).toUpperCase() + post.slice(1);
    const preTitle  = pre.charAt(0).toUpperCase()  + pre.slice(1);

    text = text.replace(new RegExp(escRe(post),               "g"), pre);
    text = text.replace(new RegExp(escRe(postTitle),          "g"), preTitle);
    text = text.replace(new RegExp(escRe(post.toUpperCase()), "g"), pre.toUpperCase());
    return text;
}

/**
 * Applies a whole-word substitution with case preservation.
 * Uses Unicode-aware boundaries (lookbehind/lookahead on \p{L}) so accented
 * characters such as ã, ç, ô do not defeat the boundary detection.
 *
 * Three variants are applied: lowercase, Title-case, UPPER-CASE.
 */
function applyWordPair(text: string, post: string, pre: string): string {
    const wb = (word: string): RegExp =>
        new RegExp(`(?<!\\p{L})${escRe(word)}(?!\\p{L})`, "gu");

    const postTitle = post.charAt(0).toUpperCase() + post.slice(1);
    const preTitle  = pre.charAt(0).toUpperCase()  + pre.slice(1);

    text = text.replace(wb(post),               pre);
    text = text.replace(wb(postTitle),          preTitle);
    text = text.replace(wb(post.toUpperCase()), pre.toUpperCase());
    return text;
}

// ─────────────────────────────────────────────────────────────────────────────
// THREE-LAYER ENGINE
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Runs the three-layer Post-AO → Pre-AO conversion engine.
 *
 * Processing order is critical:
 *   1. Layer 2 — explicit pairs (must precede root table to avoid partial matches)
 *   2. Layer 1 — root-prefix substitution
 *   3. Layer 3 — pattern rules
 */
function runThreeLayerEngine(text: string): string {
    let result = text;

    // Layer 2: verbal noun forms whose t→ç mutation hides the root from Layer 1
    for (const [post, pre] of LAYER2_PAIRS) {
        result = applyWordPair(result, post, pre);
    }

    // Layer 1: root-prefix substitution (covers all inflected forms at once)
    for (const [post, pre] of LAYER1_ROOTS) {
        result = applyRoot(result, post, pre);
    }

    // Layer 3: circumflex restoration for 3rd-person plural of crer/ver/ler/dar
    for (const [post, pre] of LAYER3_PAIRS) {
        result = applyWordPair(result, post, pre);
    }

    return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONVERSION_RULES — retained for toPostAO / detectPostAOForms back-compat
// ─────────────────────────────────────────────────────────────────────────────

export const CONVERSION_RULES: ConversionRule[] = [
    // ── ct-cluster — nouns / adjectives / verbs ───────────────────────────────
    { postAO: "ativo",          preAO: "activo",          confidence: "high" },
    { postAO: "ativos",         preAO: "activos",         confidence: "high" },
    { postAO: "ativa",          preAO: "activa",          confidence: "high" },
    { postAO: "ativas",         preAO: "activas",         confidence: "high" },
    { postAO: "atividade",      preAO: "actividade",      confidence: "high" },
    { postAO: "atividades",     preAO: "actividades",     confidence: "high" },
    { postAO: "ativar",         preAO: "activar",         confidence: "high" },
    { postAO: "ativado",        preAO: "activado",        confidence: "high" },
    { postAO: "ativação",       preAO: "activação",       confidence: "high" },
    { postAO: "inativo",        preAO: "inactivo",        confidence: "high" },
    { postAO: "inatividade",    preAO: "inactividade",    confidence: "high" },
    { postAO: "proativo",       preAO: "proactivo",       confidence: "high" },
    { postAO: "retroativo",     preAO: "retroactivo",     confidence: "high" },
    { postAO: "reativo",        preAO: "reactivo",        confidence: "high" },
    { postAO: "reator",         preAO: "reactor",         confidence: "high" },
    { postAO: "atual",          preAO: "actual",          confidence: "high" },
    { postAO: "atuais",         preAO: "actuais",         confidence: "high" },
    { postAO: "atualmente",     preAO: "actualmente",     confidence: "high" },
    { postAO: "atualizar",      preAO: "actualizar",      confidence: "high" },
    { postAO: "atualização",    preAO: "actualização",    confidence: "high" },
    { postAO: "atualizado",     preAO: "actualizado",     confidence: "high" },
    { postAO: "atualidade",     preAO: "actualidade",     confidence: "high" },
    { postAO: "ator",           preAO: "actor",           confidence: "high" },
    { postAO: "atores",         preAO: "actores",         confidence: "high" },
    { postAO: "atriz",          preAO: "actriz",          confidence: "high" },
    { postAO: "atrizes",        preAO: "actrizes",        confidence: "high" },
    { postAO: "diretor",        preAO: "director",        confidence: "high" },
    { postAO: "diretores",      preAO: "directores",      confidence: "high" },
    { postAO: "diretora",       preAO: "directora",       confidence: "high" },
    { postAO: "diretoria",      preAO: "directoria",      confidence: "high" },
    { postAO: "diretório",      preAO: "directório",      confidence: "high" },
    { postAO: "diretrizes",     preAO: "directrizes",     confidence: "high" },
    { postAO: "direto",         preAO: "directo",         confidence: "high" },
    { postAO: "direta",         preAO: "directa",         confidence: "high" },
    { postAO: "diretamente",    preAO: "directamente",    confidence: "high" },
    { postAO: "setor",          preAO: "sector",          confidence: "high" },
    { postAO: "setores",        preAO: "sectores",        confidence: "high" },
    { postAO: "objeto",         preAO: "objecto",         confidence: "high" },
    { postAO: "objetos",        preAO: "objectos",        confidence: "high" },
    { postAO: "objetivo",       preAO: "objectivo",       confidence: "high" },
    { postAO: "objetivos",      preAO: "objectivos",      confidence: "high" },
    { postAO: "objetiva",       preAO: "objectiva",       confidence: "high" },
    { postAO: "objetivamente",  preAO: "objectivamente",  confidence: "high" },
    { postAO: "projeto",        preAO: "projecto",        confidence: "high" },
    { postAO: "projetos",       preAO: "projectos",       confidence: "high" },
    { postAO: "arquiteto",      preAO: "arquitecto",      confidence: "high" },
    { postAO: "arquitetos",     preAO: "arquitectos",     confidence: "high" },
    { postAO: "arquitetura",    preAO: "arquitectura",    confidence: "high" },
    { postAO: "arquitetónico",  preAO: "arquitectónico",  confidence: "high" },
    { postAO: "afeto",          preAO: "afecto",          confidence: "high" },
    { postAO: "afetos",         preAO: "afectos",         confidence: "high" },
    { postAO: "afetivo",        preAO: "afectivo",        confidence: "high" },
    { postAO: "afetar",         preAO: "afectar",         confidence: "high" },
    { postAO: "aspeto",         preAO: "aspecto",         confidence: "high" },
    { postAO: "aspetos",        preAO: "aspectos",        confidence: "high" },
    { postAO: "exato",          preAO: "exacto",          confidence: "high" },
    { postAO: "exata",          preAO: "exacta",          confidence: "high" },
    { postAO: "exatamente",     preAO: "exactamente",     confidence: "high" },
    { postAO: "exatidão",       preAO: "exactidão",       confidence: "high" },
    { postAO: "inexato",        preAO: "inexacto",        confidence: "high" },
    { postAO: "fatura",         preAO: "factura",         confidence: "high" },
    { postAO: "faturas",        preAO: "facturas",        confidence: "high" },
    { postAO: "faturar",        preAO: "facturar",        confidence: "high" },
    { postAO: "faturação",      preAO: "facturação",      confidence: "high" },
    { postAO: "abstrato",       preAO: "abstracto",       confidence: "high" },
    { postAO: "abstratos",      preAO: "abstractos",      confidence: "high" },
    { postAO: "abstrata",       preAO: "abstracta",       confidence: "high" },
    { postAO: "abstração",      preAO: "abstracção",      confidence: "high" },
    { postAO: "contrato",       preAO: "contracto",       confidence: "high" },
    { postAO: "contratos",      preAO: "contractos",      confidence: "high" },
    { postAO: "eletricidade",   preAO: "electricidade",   confidence: "high" },
    { postAO: "elétrico",       preAO: "eléctrico",       confidence: "high" },
    { postAO: "elétrica",       preAO: "eléctrica",       confidence: "high" },
    { postAO: "eletrónica",     preAO: "electrónica",     confidence: "high" },
    { postAO: "eletrão",        preAO: "electrão",        confidence: "high" },
    // ── ct-cluster — verbal nouns (t→ç forms) ────────────────────────────────
    { postAO: "ação",           preAO: "acção",           confidence: "high" },
    { postAO: "ações",          preAO: "acções",          confidence: "high" },
    { postAO: "reação",         preAO: "reacção",         confidence: "high" },
    { postAO: "reações",        preAO: "reacções",        confidence: "high" },
    { postAO: "direção",        preAO: "direcção",        confidence: "high" },
    { postAO: "direções",       preAO: "direcções",       confidence: "high" },
    { postAO: "deteção",        preAO: "detecção",        confidence: "high" },
    { postAO: "deteções",       preAO: "detecções",       confidence: "high" },
    { postAO: "coleção",        preAO: "colecção",        confidence: "high" },
    { postAO: "coleções",       preAO: "colecções",       confidence: "high" },
    { postAO: "seleção",        preAO: "selecção",        confidence: "high" },
    { postAO: "seleções",       preAO: "selecções",       confidence: "high" },
    { postAO: "infeção",        preAO: "infecção",        confidence: "high" },
    { postAO: "infeções",       preAO: "infecções",       confidence: "high" },
    { postAO: "perceção",       preAO: "percepção",       confidence: "high" },
    { postAO: "perceções",      preAO: "percepções",      confidence: "high" },
    { postAO: "conceção",       preAO: "concepção",       confidence: "high" },
    { postAO: "conceções",      preAO: "concepções",      confidence: "high" },
    { postAO: "receção",        preAO: "recepção",        confidence: "high" },
    { postAO: "receções",       preAO: "recepções",       confidence: "high" },
    { postAO: "redação",        preAO: "redacção",        confidence: "high" },
    { postAO: "redações",       preAO: "redacções",       confidence: "high" },
    { postAO: "transação",      preAO: "transacção",      confidence: "high" },
    { postAO: "transações",     preAO: "transacções",     confidence: "high" },
    { postAO: "atração",        preAO: "atracção",        confidence: "high" },
    { postAO: "atrações",       preAO: "atracções",       confidence: "high" },
    { postAO: "distração",      preAO: "distracção",      confidence: "high" },
    { postAO: "distrações",     preAO: "distracções",     confidence: "high" },
    { postAO: "satisfação",     preAO: "satisfacção",     confidence: "high" },
    { postAO: "satisfações",    preAO: "satisfacções",    confidence: "high" },
    { postAO: "contração",      preAO: "contracção",      confidence: "high" },
    { postAO: "contrações",     preAO: "contracções",     confidence: "high" },
    { postAO: "extração",       preAO: "extracção",       confidence: "high" },
    { postAO: "extrações",      preAO: "extracções",      confidence: "high" },
    { postAO: "injeção",        preAO: "injecção",        confidence: "high" },
    { postAO: "injeções",       preAO: "injecções",       confidence: "high" },
    { postAO: "proteção",       preAO: "protecção",       confidence: "high" },
    { postAO: "proteções",      preAO: "protecções",      confidence: "high" },
    { postAO: "exceção",        preAO: "excepção",        confidence: "high" },
    { postAO: "exceções",       preAO: "excepções",       confidence: "high" },
    { postAO: "deceção",        preAO: "decepção",        confidence: "high" },
    { postAO: "interseção",     preAO: "intersecção",     confidence: "high" },
    { postAO: "contraceção",    preAO: "contracepção",    confidence: "high" },
    { postAO: "interceção",     preAO: "intercepção",     confidence: "high" },
    { postAO: "rececionar",     preAO: "recepcionar",     confidence: "high" },
    { postAO: "rececionista",   preAO: "recepcionista",   confidence: "high" },
    { postAO: "dececionar",     preAO: "decepcionar",     confidence: "high" },
    { postAO: "recetivo",       preAO: "receptivo",       confidence: "high" },
    { postAO: "recetivos",      preAO: "receptivos",      confidence: "high" },
    { postAO: "recetiva",       preAO: "receptiva",       confidence: "high" },
    { postAO: "recetivas",      preAO: "receptivas",      confidence: "high" },
    {
        postAO: "seção",
        preAO:  "secção",
        confidence: "high",
        notes: "PT-BR form — restore PT-PT secção",
    },
    // ── pt-cluster ────────────────────────────────────────────────────────────
    { postAO: "ótimo",          preAO: "óptimo",          confidence: "high" },
    { postAO: "ótimos",         preAO: "óptimos",         confidence: "high" },
    { postAO: "ótima",          preAO: "óptima",          confidence: "high" },
    { postAO: "ótimas",         preAO: "óptimas",         confidence: "high" },
    { postAO: "otimizar",       preAO: "optimizar",       confidence: "high" },
    { postAO: "otimização",     preAO: "optimização",     confidence: "high" },
    { postAO: "otimismo",       preAO: "optimismo",       confidence: "high" },
    { postAO: "otimista",       preAO: "optimista",       confidence: "high" },
    { postAO: "batismo",        preAO: "baptismo",        confidence: "high" },
    { postAO: "batismos",       preAO: "baptismos",       confidence: "high" },
    { postAO: "batismal",       preAO: "baptismal",       confidence: "high" },
    { postAO: "batismais",      preAO: "baptismais",      confidence: "high" },
    { postAO: "batista",        preAO: "baptista",        confidence: "high" },
    { postAO: "batizar",        preAO: "baptizar",        confidence: "high" },
    { postAO: "adoção",         preAO: "adopção",         confidence: "high" },
    { postAO: "adoções",        preAO: "adopções",        confidence: "high" },
    { postAO: "adotar",         preAO: "adoptar",         confidence: "high" },
    { postAO: "adotado",        preAO: "adoptado",        confidence: "high" },
    { postAO: "adotada",        preAO: "adoptada",        confidence: "high" },
    { postAO: "adotados",       preAO: "adoptados",       confidence: "high" },
    { postAO: "adotadas",       preAO: "adoptadas",       confidence: "high" },
    { postAO: "adotou",         preAO: "adoptou",         confidence: "high" },
    { postAO: "adotaram",       preAO: "adoptaram",       confidence: "high" },
    { postAO: "adotarão",       preAO: "adoptarão",       confidence: "high" },
    { postAO: "adotaria",       preAO: "adoptaria",       confidence: "high" },
    { postAO: "adotando",       preAO: "adoptando",       confidence: "high" },
    { postAO: "adote",          preAO: "adopte",          confidence: "high" },
    { postAO: "adotem",         preAO: "adoptem",         confidence: "high" },
    // ── aceitar/aceite ────────────────────────────────────────────────────────
    { postAO: "aceito",         preAO: "aceite",          confidence: "high" },
    { postAO: "aceitos",        preAO: "aceites",         confidence: "high" },
    { postAO: "aceita",         preAO: "aceite",          confidence: "medium" },
    // ── excepto ───────────────────────────────────────────────────────────────
    { postAO: "exceto",         preAO: "excepto",         confidence: "high" },
    { postAO: "excetos",        preAO: "exceptos",        confidence: "high" },
    { postAO: "exceta",         preAO: "excepta",         confidence: "high" },
    { postAO: "excetas",        preAO: "exceptas",        confidence: "high" },
    // ── Layer 3 verbal forms ──────────────────────────────────────────────────
    { postAO: "creem",          preAO: "crêem",           confidence: "high" },
    { postAO: "veem",           preAO: "vêem",            confidence: "high" },
    { postAO: "leem",           preAO: "lêem",            confidence: "high" },
    { postAO: "deem",           preAO: "dêem",            confidence: "high" },
    // ── Medium confidence — context-dependent ─────────────────────────────────
    {
        postAO: "fato",
        preAO:  "facto",
        confidence: "medium",
        notes: 'VERIFY: "fato" = suit/clothing in PT-PT (keep) OR fact (→ facto).',
    },
    {
        postAO: "fatos",
        preAO:  "factos",
        confidence: "medium",
        notes: "VERIFY: suits (fatos) or facts (factos)?",
    },
    {
        postAO: "ato",
        preAO:  "acto",
        confidence: "medium",
        notes: "VERIFY: act/deed → acto. Check for acronym usage.",
    },
    {
        postAO: "atos",
        preAO:  "actos",
        confidence: "medium",
        notes: "VERIFY: acts/deeds → actos. Check for acronym usage.",
    },
    // ── Lexical (PT-BR → PT-PT) ───────────────────────────────────────────────
    {
        postAO: "governança",
        preAO:  "governação",
        confidence: "high",
        notes: 'PT-BR term — PT-PT equivalent is "governação"',
    },
    {
        postAO: "gerenciamento",
        preAO:  "gestão",
        confidence: "high",
        notes: 'PT-BR term — PT-PT uses "gestão"',
    },
    {
        postAO: "implementar",
        preAO:  "executar",
        confidence: "medium",
        notes: 'PT-PT often prefers "executar" or "aplicar" in legal/admin contexts.',
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// LEXICAL VARIANTS (PT-BR → PT-PT)
// ─────────────────────────────────────────────────────────────────────────────

export const LEXICAL_VARIANTS: LexicalVariant[] = [
    // ── Technology ────────────────────────────────────────────────────────────
    { ptBR: "arquivo",        ptPT: "ficheiro",           domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "banco de dados", ptPT: "base de dados",      domains: ["technology"],                  register: "formal",   confidence: "high" },
    { ptBR: "celular",        ptPT: "telemóvel",          domains: ["technology", "general"],        register: "neutral",  confidence: "high" },
    { ptBR: "clipe",          ptPT: "clip",               domains: ["technology", "office"],         register: "neutral",  confidence: "medium" },
    { ptBR: "deletar",        ptPT: "eliminar",           domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "download",       ptPT: "transferência",      domains: ["technology"],                  register: "neutral",  confidence: "medium", notes: "PT-PT prefers native term in formal contexts" },
    { ptBR: "logar",          ptPT: "iniciar sessão",     domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "mouse",          ptPT: "rato",               domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "notebook",       ptPT: "portátil",           domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "senha",          ptPT: "palavra-passe",      domains: ["technology"],                  register: "neutral",  confidence: "high" },
    { ptBR: "tela",           ptPT: "ecrã",               domains: ["technology"],                  register: "neutral",  confidence: "high" },
    // ── Legal / Public Administration ─────────────────────────────────────────
    { ptBR: "governança",     ptPT: "governação",         domains: ["legal", "public_admin"],        register: "formal",   confidence: "high" },
    { ptBR: "gerenciamento",  ptPT: "gestão",             domains: ["legal", "public_admin", "business"], register: "formal", confidence: "high" },
    { ptBR: "accountability", ptPT: "responsabilização",  domains: ["legal", "public_admin"],        register: "formal",   confidence: "high",   notes: "PT-PT uses native term" },
    { ptBR: "compliance",     ptPT: "conformidade",       domains: ["legal", "business"],            register: "formal",   confidence: "high",   notes: "PT-PT avoids anglicism in formal docs" },
    { ptBR: "framework",      ptPT: "quadro",             domains: ["legal", "public_admin"],        register: "formal",   confidence: "medium", notes: '"framework" acceptable in technical contexts' },
    { ptBR: "guidelines",     ptPT: "orientações",        domains: ["legal", "public_admin"],        register: "formal",   confidence: "high",   notes: 'Pre-AO PT-PT: "directrizes" also used' },
    { ptBR: "stakeholder",    ptPT: "parte interessada",  domains: ["legal", "public_admin"],        register: "formal",   confidence: "high" },
    { ptBR: "projeto de lei", ptPT: "projecto de lei",    domains: ["legal", "public_admin"],        register: "formal",   confidence: "high",   notes: "Pre-AO: projecto" },
    // ── Education / Academia ──────────────────────────────────────────────────
    { ptBR: "pesquisa",       ptPT: "investigação",       domains: ["education", "academia"],        register: "formal",   confidence: "high",   notes: "Strong marker of PT-BR in academic texts" },
    { ptBR: "pesquisador",    ptPT: "investigador",       domains: ["education", "academia"],        register: "formal",   confidence: "high" },
    { ptBR: "pesquisadora",   ptPT: "investigadora",      domains: ["education", "academia"],        register: "formal",   confidence: "high" },
    { ptBR: "graduação",      ptPT: "licenciatura",       domains: ["education"],                   register: "formal",   confidence: "high",   notes: "Significant structural difference" },
    { ptBR: "monografia",     ptPT: "dissertação",        domains: ["education", "academia"],        register: "formal",   confidence: "medium", notes: "Usage differs by institution" },
    // ── Healthcare ────────────────────────────────────────────────────────────
    { ptBR: "remédio",        ptPT: "medicamento",        domains: ["healthcare"],                  register: "formal",   confidence: "high",   notes: "PT-BR informal, PT-PT formal term" },
    { ptBR: "paciente",       ptPT: "doente",             domains: ["healthcare"],                  register: "formal",   confidence: "medium", notes: '"paciente" also used in PT-PT, "doente" is traditional' },
    { ptBR: "plano de saúde", ptPT: "seguro de saúde",    domains: ["healthcare"],                  register: "formal",   confidence: "high" },
    { ptBR: "pronto-socorro", ptPT: "urgência",           domains: ["healthcare"],                  register: "neutral",  confidence: "high" },
    // ── Business / Finance ────────────────────────────────────────────────────
    { ptBR: "nota fiscal",    ptPT: "fatura",             domains: ["business", "finance"],          register: "formal",   confidence: "high" },
    { ptBR: "boleto",         ptPT: "documento de cobrança", domains: ["finance"],                  register: "formal",   confidence: "high",   notes: "PT-BR specific term" },
    { ptBR: "estoque",        ptPT: "stock",              domains: ["business"],                    register: "formal",   confidence: "high" },
    { ptBR: "feedback",       ptPT: "retorno",            domains: ["business"],                    register: "formal",   confidence: "medium", notes: '"feedback" widely accepted in PT-PT informal business' },
    { ptBR: "CEO",            ptPT: "presidente executivo", domains: ["business"],                  register: "formal",   confidence: "medium", notes: "PT-PT formal docs avoid acronym" },
    // ── Transport / Daily Life ────────────────────────────────────────────────
    { ptBR: "ônibus",         ptPT: "autocarro",          domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "trem",           ptPT: "comboio",            domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "metrô",          ptPT: "metro",              domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "banheiro",       ptPT: "casa de banho",      domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "geladeira",      ptPT: "frigorífico",        domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "sorvete",        ptPT: "gelado",             domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "suco",           ptPT: "sumo",               domains: ["general"],                     register: "neutral",  confidence: "high" },
    { ptBR: "abacaxi",        ptPT: "ananás",             domains: ["general"],                     register: "neutral",  confidence: "high" },
];

// ─────────────────────────────────────────────────────────────────────────────
// CONVERTER ENGINE — exported functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Applies a single word substitution respecting word boundaries and case.
 * Used internally by toPostAO and detectPostAOForms (CONVERSION_RULES path).
 */
function applySubstitution(text: string, from: string, to: string): string {
    const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escaped}\\b`, "gi");
    return text.replace(regex, (match) => {
        if (match === match.toUpperCase()) return to.toUpperCase();
        if (match[0] === match[0].toUpperCase()) {
            return to.charAt(0).toUpperCase() + to.slice(1);
        }
        return to;
    });
}

/**
 * Convert Post-AO text to Pre-AO using the three-layer rule engine.
 * All rules are applied automatically (high confidence).
 * Medium-confidence items from CONVERSION_RULES are flagged for review.
 */
export function toPreAO(text: string): ConversionResult {
    // Run the three-layer engine for all high-confidence conversions
    const converted = runThreeLayerEngine(text);

    // Collect medium-confidence flags from CONVERSION_RULES
    const flags: DetectedForm[] = [];
    for (const rule of CONVERSION_RULES) {
        if (rule.confidence !== "medium") continue;
        if (rule.postAO === rule.preAO) continue;
        const escaped = rule.postAO.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escaped}\\b`, "gi");
        let match: RegExpExecArray | null;
        while ((match = regex.exec(converted)) !== null) {
            flags.push({
                word:      match[0],
                position:  match.index,
                postAOForm: rule.postAO,
                preAOForm:  rule.preAO,
                confidence: "medium",
                notes:      rule.notes,
            });
        }
    }

    const changeCount = converted !== text ? 1 : 0;
    return { text: converted, flags, changeCount };
}

/**
 * Convert Pre-AO text to Post-AO (for modernising legacy documents).
 * Reverses the CONVERSION_RULES list.
 */
export function toPostAO(text: string): ConversionResult {
    let result = text;
    const flags: DetectedForm[] = [];
    let changeCount = 0;

    for (const rule of CONVERSION_RULES) {
        if (rule.postAO === rule.preAO) continue;

        if (rule.confidence === "high") {
            const newText = applySubstitution(result, rule.preAO, rule.postAO);
            if (newText !== result) changeCount++;
            result = newText;
        } else {
            const escaped = rule.preAO.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const regex = new RegExp(`\\b${escaped}\\b`, "gi");
            let match: RegExpExecArray | null;
            while ((match = regex.exec(result)) !== null) {
                flags.push({
                    word:      match[0],
                    position:  match.index,
                    preAOForm:  rule.preAO,
                    postAOForm: rule.postAO,
                    confidence: "medium",
                    notes:      rule.notes,
                });
            }
        }
    }

    return { text: result, flags, changeCount };
}

/**
 * Detect Post-AO forms in a segment without converting.
 * Used by spell-checker integration in Pre-AO projects.
 */
export function detectPostAOForms(text: string): DetectedForm[] {
    const detected: DetectedForm[] = [];

    for (const rule of CONVERSION_RULES) {
        if (rule.postAO === rule.preAO) continue;
        const escaped = rule.postAO.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escaped}\\b`, "gi");
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
            detected.push({
                word:      match[0],
                position:  match.index,
                postAOForm: rule.postAO,
                preAOForm:  rule.preAO,
                confidence: rule.confidence,
                notes:      rule.notes,
            });
        }
    }

    return detected;
}

/**
 * Detect PT-BR lexical variants in MT output.
 * Used to flag non-PT-PT vocabulary regardless of orthographic standard.
 */
export function detectBRVariants(
    text: string,
    domains: string[] = [],
): DetectedForm[] {
    const detected: DetectedForm[] = [];

    for (const variant of LEXICAL_VARIANTS) {
        if (
            domains.length > 0 &&
            !variant.domains.some((d) => domains.includes(d))
        ) continue;

        const escaped = variant.ptBR.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = new RegExp(`\\b${escaped}\\b`, "gi");
        let match: RegExpExecArray | null;
        while ((match = regex.exec(text)) !== null) {
            detected.push({
                word:      match[0],
                position:  match.index,
                postAOForm: variant.ptBR,
                preAOForm:  variant.ptPT,
                confidence: variant.confidence,
                notes:      variant.notes ?? `PT-BR term — PT-PT equivalent: "${variant.ptPT}"`,
            });
        }
    }

    return detected;
}

/**
 * Full pipeline: detect and flag all issues in a segment.
 * Used by the editor's Pre-AO quality gate.
 */
export function analyseSegment(
    text: string,
    orthographicStandard: "pre_ao" | "post_ao",
    domains: string[] = [],
): {
    postAOForms: DetectedForm[];
    brVariants: DetectedForm[];
    totalIssues: number;
} {
    const postAOForms =
        orthographicStandard === "pre_ao" ? detectPostAOForms(text) : [];
    const brVariants = detectBRVariants(text, domains);

    return {
        postAOForms,
        brVariants,
        totalIssues: postAOForms.length + brVariants.length,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// QUICK STATS (for UI display)
// ─────────────────────────────────────────────────────────────────────────────

export const CONVERTER_STATS = {
    layer1Roots:           LAYER1_ROOTS.length,
    layer2Pairs:           LAYER2_PAIRS.length,
    layer3Patterns:        LAYER3_PAIRS.length,
    totalRules:            CONVERSION_RULES.length,
    highConfidenceRules:   CONVERSION_RULES.filter((r) => r.confidence === "high").length,
    mediumConfidenceRules: CONVERSION_RULES.filter((r) => r.confidence === "medium").length,
    lexicalVariants:       LEXICAL_VARIANTS.length,
    version:               "2.0.0",
    basedOn:               "Acordo Ortográfico da Língua Portuguesa 1990/2009",
};

// ─────────────────────────────────────────────────────────────────────────────
// TEST CASES (not exported — for documentation and manual verification)
// ─────────────────────────────────────────────────────────────────────────────

/*
const _TEST_CASES: Array<[input: string, expected: string, note?: string]> = [
    // Layer 1 — root table
    ["ativo",          "activo",         "ct-cluster via root ativ"],
    ["atividade",      "actividade",     "ct-cluster via root ativ, inflected"],
    ["proativo",       "proactivo",      "ct-cluster, compound prefix pro-"],
    ["retroativo",     "retroactivo",    "ct-cluster, compound prefix retro-"],
    ["adotou",         "adoptou",        "pt-cluster via root adot"],
    ["fatura",         "factura",        "ct-cluster via root fatur"],
    ["exato",          "exacto",         "ct-cluster via root exat"],
    ["exatamente",     "exactamente",    "ct-cluster via root exat, adverb"],
    ["direto",         "directo",        "ct-cluster via root diret"],
    ["diretor",        "director",       "ct-cluster via root diret, agent noun"],
    ["ótimo",          "óptimo",         "pt-cluster via root ótim"],
    ["otimismo",       "optimismo",      "pt-cluster via root otimis"],
    ["batismo",        "baptismo",       "pt-cluster via root batism"],
    ["projeto",        "projecto",       "ct-cluster via root projet"],
    ["objetivo",       "objectivo",      "ct-cluster via root objetiv"],
    ["setor",          "sector",         "ct-cluster via root seto"],
    // Layer 2 — explicit pairs (verbal nouns)
    ["adoção",         "adopção",        "Layer 2 singular"],
    ["ações",          "acções",         "Layer 2 plural before singular"],
    ["direção",        "direcção",       "Layer 2 singular"],
    // Layer 3 — pattern rules (circumflex)
    ["creem",          "crêem",          "Layer 3 — 3rd-pl crer"],
    ["veem",           "vêem",           "Layer 3 — 3rd-pl ver"],
    // Protected — must pass through unchanged
    ["contacto",       "contacto",       "preserved — already Pre-AO"],
    // Case preservation
    ["Ativo",          "Activo",         "Title case"],
    ["ATIVO",          "ACTIVO",         "UPPER CASE"],
];
*/
